import { useEffect } from "react";

/**
 * Lightweight per-page SEO hook for LocalLawFinder.
 * Updates document.title + key meta tags and canonical URL on mount.
 *
 * Site-wide defaults (Organization, WebSite, LegalService JSON-LD, theme,
 * social handles, manifest pointers) live in /public/index.html so crawlers
 * see them immediately on first byte. This hook layers per-page specifics.
 */
const SITE_NAME = "LocalLawFinder";
const SITE_ORIGIN = "https://locallawfinder.com";

function setTag(selector, attr, value) {
    if (!value) return;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement(selector.startsWith("meta") ? "meta" : "link");
        // selector looks like: meta[name="description"]  or  meta[property="og:title"]  or link[rel="canonical"]
        const m = selector.match(/\[([^=]+)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
}

export default function useSEO({ title, description, path = "/", image, noindex = false } = {}) {
    useEffect(() => {
        const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
        document.title = fullTitle;

        const url = `${SITE_ORIGIN}${path}`;
        const ogImage = image || `${SITE_ORIGIN}/locallawfinder-logo.svg`;

        setTag('meta[name="description"]', "content", description || "");
        setTag('link[rel="canonical"]', "href", url);

        // Open Graph
        setTag('meta[property="og:type"]', "content", "website");
        setTag('meta[property="og:site_name"]', "content", SITE_NAME);
        setTag('meta[property="og:title"]', "content", fullTitle);
        setTag('meta[property="og:description"]', "content", description || "");
        setTag('meta[property="og:url"]', "content", url);
        setTag('meta[property="og:image"]', "content", ogImage);

        // Twitter
        setTag('meta[name="twitter:card"]', "content", "summary_large_image");
        setTag('meta[name="twitter:title"]', "content", fullTitle);
        setTag('meta[name="twitter:description"]', "content", description || "");
        setTag('meta[name="twitter:image"]', "content", ogImage);

        // Robots — page-level override (used by /results, /attorney/dashboard, etc.)
        setTag(
            'meta[name="robots"]',
            "content",
            noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
        );
    }, [title, description, path, image, noindex]);
}
